import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, addDoc, collection, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useConfig } from '../contexts/ConfigContext';
import LoadingAnimation from './LoadingAnimation';

const emptyBlock = { type: 'text', content: '', url: '', caption: '' };

const newBlockId = () => `b_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const AdminReflectionEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const config = useConfig();
  const isEditing = Boolean(id);

  const [authLoading, setAuthLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(isEditing);

  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [summary, setSummary] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [status, setStatus] = useState('published');
  const [scheduledAt, setScheduledAt] = useState('');

  const [coverUploading, setCoverUploading] = useState(false);
  const [coverProgress, setCoverProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login', { state: { from: location.pathname } });
        return;
      }
      if (config.configLoaded && config.adminEmails.length > 0 && !config.adminEmails.includes(user.email)) {
        navigate('/');
        return;
      }
      if (config.configLoaded) {
        setAuthLoading(false);
        if (isEditing) {
          await loadReflection();
          setDataLoading(false);
        }
      }
    });
    return () => unsubscribe();
  }, [config.configLoaded, config.adminEmails]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReflection = async () => {
    const snap = await getDoc(doc(db, 'reflections', id));
    if (!snap.exists()) { navigate('/admin/reflections'); return; }
    const data = snap.data();
    setTitle(data.title || '');
    setAuthor(data.author || '');
    setSummary(data.summary || '');
    setCoverImageUrl(data.cover_image_url || '');
    setStatus(data.status || 'published');
    if (data.scheduled_at) {
      const d = data.scheduled_at.toDate ? data.scheduled_at.toDate() : new Date(data.scheduled_at);
      setScheduledAt(d.toISOString().slice(0, 16));
    }
    // Ensure every block has a stable id
    setBlocks((data.blocks || []).map(b => ({ ...b, id: b.id || newBlockId() })));
  };

  const uploadCoverImage = (file) => {
    if (!file) return;
    setCoverUploading(true);
    const storageRef = ref(storage, `reflections/covers/${Date.now()}_${file.name}`);
    const task = uploadBytesResumable(storageRef, file);
    task.on('state_changed',
      snap => setCoverProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
      err => { console.error(err); setCoverUploading(false); },
      () => {
        getDownloadURL(task.snapshot.ref).then(url => {
          setCoverImageUrl(url);
          setCoverUploading(false);
        });
      }
    );
  };

  // Uses blockId (not index) so image URL persists when blocks are reordered
  const uploadBlockImage = (file, blockId) => {
    if (!file) return;
    const storageRef = ref(storage, `reflections/blocks/${Date.now()}_${file.name}`);
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, uploading: true } : b));

    const task = uploadBytesResumable(storageRef, file);
    task.on('state_changed', null,
      err => {
        console.error(err);
        setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, uploading: false } : b));
      },
      () => {
        getDownloadURL(task.snapshot.ref).then(url => {
          setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, url, uploading: false } : b));
        });
      }
    );
  };

  const addBlock = () => setBlocks(prev => [...prev, { ...emptyBlock, id: newBlockId() }]);

  const updateBlock = (blockId, field, value) => {
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, [field]: value } : b));
  };

  const removeBlock = (blockId) => setBlocks(prev => prev.filter(b => b.id !== blockId));

  const moveBlock = (index, direction) => {
    const updated = [...blocks];
    const swapWith = index + direction;
    if (swapWith < 0 || swapWith >= updated.length) return;
    [updated[index], updated[swapWith]] = [updated[swapWith], updated[index]];
    setBlocks(updated);
  };

  const handleSave = async () => {
    if (!title.trim()) { setSaveMsg('Title is required.'); return; }
    if (status === 'scheduled' && !scheduledAt) { setSaveMsg('Publish date/time is required for scheduled status.'); return; }
    setSaving(true);
    setSaveMsg('');
    try {
      const cleanBlocks = blocks.map(({ uploading, ...rest }) => rest);
      const data = {
        title: title.trim(),
        author: author.trim(),
        summary: summary.trim(),
        cover_image_url: coverImageUrl,
        blocks: cleanBlocks,
        status,
        scheduled_at: status === 'scheduled' && scheduledAt
          ? Timestamp.fromDate(new Date(scheduledAt))
          : null,
        updated_at: Timestamp.fromDate(new Date())
      };
      if (!isEditing) {
        data.created_at = Timestamp.fromDate(new Date());
        delete data.updated_at;
      }

      if (isEditing) {
        await setDoc(doc(db, 'reflections', id), data, { merge: true });
      } else {
        await addDoc(collection(db, 'reflections'), data);
      }
      setSaveMsg('Saved!');
      if (!isEditing) navigate('/admin/reflections');
    } catch (err) {
      console.error(err);
      setSaveMsg('Error saving.');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || dataLoading) return <div style={{ minHeight: '100vh' }}><LoadingAnimation /></div>;

  return (
    <div className="stt-admin-page">
      <a href="/admin/reflections" className="stt-admin-back">← Back to Reflections</a>
      <h1 className="stt-admin-title">{isEditing ? 'Edit Reflection' : 'New Reflection'}</h1>
      <hr />

      {/* Metadata */}
      <div className="stt-admin-card">
        <h2 style={{ fontFamily: 'var(--font-header)', fontSize: '1.1rem', marginBottom: '16px', color: 'var(--navy)' }}>Details</h2>

        <div className="mb-3">
          <label className="stt-form-label">Title *</label>
          <input type="text" className="form-control" value={title} onChange={e => setTitle(e.target.value)} />
        </div>

        <div className="mb-3">
          <label className="stt-form-label">Author</label>
          <input type="text" className="form-control" value={author} onChange={e => setAuthor(e.target.value)} placeholder="e.g. Fr. Peter" />
        </div>

        <div className="mb-3">
          <label className="stt-form-label">Summary</label>
          <p className="stt-form-hint">Shown on the reflections list page. 1–2 sentences.</p>
          <textarea className="form-control" rows={2} value={summary} onChange={e => setSummary(e.target.value)} />
        </div>

        <div className="mb-3">
          <label className="stt-form-label">Cover Image</label>
          <p className="stt-form-hint">Optional. Displayed at the top of the article.</p>
          {coverImageUrl && (
            <div style={{ marginBottom: '10px' }}>
              <img src={coverImageUrl} alt="Cover" style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'cover' }} />
              <button
                style={{ display: 'block', marginTop: '6px', background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontSize: '0.82rem' }}
                onClick={() => setCoverImageUrl('')}
              >
                Remove cover image
              </button>
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            className="form-control"
            onChange={e => uploadCoverImage(e.target.files[0])}
            disabled={coverUploading}
          />
          {coverUploading && (
            <p className="stt-upload-progress">Uploading… {coverProgress}%</p>
          )}
        </div>
      </div>

      {/* Block editor */}
      <div className="stt-admin-card">
        <h2 style={{ fontFamily: 'var(--font-header)', fontSize: '1.1rem', marginBottom: '16px', color: 'var(--navy)' }}>Article Body</h2>
        <p className="stt-form-hint" style={{ marginBottom: '20px' }}>
          Build the article by adding blocks. Each block is a paragraph of text, a subheading, or an image.
        </p>

        <div className="stt-block-editor">
          {blocks.map((block, i) => (
            <div key={block.id} className="stt-block-item">
              <div className="stt-block-controls">
                <select value={block.type} onChange={e => updateBlock(block.id, 'type', e.target.value)}>
                  <option value="text">Paragraph</option>
                  <option value="heading">Subheading</option>
                  <option value="image">Image</option>
                </select>
                <button className="stt-block-move" onClick={() => moveBlock(i, -1)} disabled={i === 0}>↑</button>
                <button className="stt-block-move" onClick={() => moveBlock(i, 1)} disabled={i === blocks.length - 1}>↓</button>
                <button className="stt-block-remove" onClick={() => removeBlock(block.id)}>✕</button>
              </div>

              {block.type === 'text' && (
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder="Write your paragraph here..."
                  value={block.content}
                  onChange={e => updateBlock(block.id, 'content', e.target.value)}
                />
              )}

              {block.type === 'heading' && (
                <input
                  type="text"
                  className="form-control"
                  placeholder="Subheading text..."
                  value={block.content}
                  onChange={e => updateBlock(block.id, 'content', e.target.value)}
                />
              )}

              {block.type === 'image' && (
                <>
                  {block.url && (
                    <div style={{ marginBottom: '10px' }}>
                      <img src={block.url} alt="" style={{ maxWidth: '100%', maxHeight: '160px', objectFit: 'contain' }} />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control mb-2"
                    onChange={e => uploadBlockImage(e.target.files[0], block.id)}
                    disabled={block.uploading}
                  />
                  {block.uploading && <p className="stt-upload-progress">Uploading image…</p>}
                  <input
                    type="text"
                    className="form-control mb-2"
                    placeholder="Caption (optional)"
                    value={block.caption}
                    onChange={e => updateBlock(block.id, 'caption', e.target.value)}
                  />
                  <div className="row">
                    <div className="col-6">
                      <select
                        className="form-select form-select-sm"
                        value={block.size || 'medium'}
                        onChange={e => updateBlock(block.id, 'size', e.target.value)}
                      >
                        <option value="small">Small (25%)</option>
                        <option value="medium">Medium (50%)</option>
                        <option value="large">Large (full width)</option>
                      </select>
                    </div>
                    {(block.size || 'medium') !== 'large' && (
                      <div className="col-6">
                        <select
                          className="form-select form-select-sm"
                          value={block.align || 'center'}
                          onChange={e => updateBlock(block.id, 'align', e.target.value)}
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                        </select>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}

          <button className="btn btn-outline-secondary" onClick={addBlock}>
            + Add Block
          </button>
        </div>
      </div>

      {/* Visibility */}
      <div className="stt-admin-card">
        <h2 style={{ fontFamily: 'var(--font-header)', fontSize: '1.1rem', marginBottom: '16px', color: 'var(--navy)' }}>Visibility</h2>

        <div className="mb-3">
          <label className="stt-form-label">Status</label>
          <select className="form-control" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
          </select>
        </div>

        {status === 'scheduled' && (
          <div className="mb-3">
            <label className="stt-form-label">Publish Date &amp; Time *</label>
            <input
              type="datetime-local"
              className="form-control"
              value={scheduledAt}
              onChange={e => setScheduledAt(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
        <button className="btn stt-btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Update Reflection' : 'Publish Reflection'}
        </button>
        {saveMsg && (
          <span className={`stt-save-message ${saveMsg === 'Saved!' ? 'success' : 'error'}`}>
            {saveMsg}
          </span>
        )}
      </div>
    </div>
  );
};

export default AdminReflectionEditor;
